import React, {
  useEffect,
  useState,
  useRef,
  ReactNode,
  RefObject,
  CSSProperties,
  HTMLAttributes,
} from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";
import DismissIcon from "./icons/DismissIcon";
import { onDesktop } from "./layout/Breakpoints";
import { getScrollbarWidth } from "./utils/ScrollBarWidth";

interface ModalWrapperProps extends HTMLAttributes<HTMLDivElement> {
  floatingCloseButton: boolean;
  invertClose?: boolean;
}

const ModalWrapper = styled((props: ModalWrapperProps) => {
  const { floatingCloseButton: _, invertClose: __, ...rest } = props;

  return <div {...rest} />;
})`
  z-index: 5;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 0.5em;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;

  ${onDesktop`
    padding: 1em;
  `}

  ${(props) =>
    props.floatingCloseButton &&
    css`
      ${ModalElement} {
        padding: 0;
        position: relative;
      }

      ${ModalHeader} {
        h2 {
          display: none;
        }

        button {
          position: absolute;
          right: 0.5em;
          top: 0.5em;
          background: white;
          width: 2.5em;
          height: 2.5em;
          border-radius: 50%;
          z-index: 2;
          box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    `}

    ${(props) =>
    props.floatingCloseButton &&
    props.invertClose &&
    css`
      ${ModalHeader} button {
        background: transparent;

        svg path {
          fill: white;
        }
      }
    `}
`;

const ModalContainer = styled.div`
  max-width: 720px;
  margin: auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  ${onDesktop`
    padding: 0 1em;
  `}
`;

const ModalElement = styled.div`
  background-color: white;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.25);
  border-radius: 0.3em;
  padding: 0.5em 1em;
  margin: auto;
  flex: 0 0 auto;
  max-width: 100%;
  box-sizing: border-box;

  @media screen and (min-width: 30em) {
    max-width: 30em;
  }

  ${onDesktop`
    padding: 1em 2em;
    max-width: 40em;
  `}
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.6em;
  margin-bottom: 1em;

  button {
    background: none;
    border: none;
    color: black;
    padding: 0;
    cursor: pointer;
    margin-left: 1em;
  }

  h2 {
    margin: 0;
  }
`;

interface ModalProps {
  button?: RefObject<HTMLElement | null> | null;
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  title: string;
  floatingCloseButton?: boolean;
  invertClose?: boolean;
}

export default function Modal({
  button,
  children,
  isOpen,
  onClose,
  className,
  title,
  floatingCloseButton,
  invertClose,
}: ModalProps) {
  const [disableBodyScroll, setDisableBodyScroll] = useState(false);

  const ANIMATION_TIMEOUT_MS = 250;

  const [modalWrapperStyle, setModalWrapperStyle] = useState({
    opacity: 0,
    display: "none",
    transition: `opacity ${ANIMATION_TIMEOUT_MS / 1000}s ease`,
  });

  const [modalPlaceholderStyle, setModalPlaceholderStyle] =
    useState<CSSProperties>({
      boxSizing: "border-box",
      position: "fixed",
      transition: `transform ${ANIMATION_TIMEOUT_MS / 1000}s ease`,
      display: "none",
    });

  const modal = useRef<HTMLDivElement | null>(null);

  const [modalStyle, setModalStyle] = useState({});

  function getModalPlaceholderPlacement(buttonRect: DOMRect | null) {
    if (!modal.current) return;
    if (!buttonRect) return;

    const modalRect = modal.current.getBoundingClientRect();

    const buttonCenterX = buttonRect
      ? buttonRect.left + buttonRect.width / 2
      : document.body.clientWidth / 2;
    const modalCenterX = modalRect.left + modalRect.width / 2;
    const buttonCenterY = buttonRect
      ? buttonRect.top + buttonRect.height / 2
      : document.body.clientHeight / 2;
    const modalCenterY = modalRect.top + modalRect.height / 2;

    const scaleX = buttonRect ? buttonRect.width / modalRect.width : 0;
    const scaleY = buttonRect ? buttonRect.height / modalRect.height : 0;
    const translateY = buttonCenterY - modalCenterY;
    const translateX = buttonCenterX - modalCenterX;

    return {
      left: `${modalRect.left}px`,
      top: `${modalRect.top}px`,
      width: `${modalRect.width}px`,
      height: `${modalRect.height}px`,
      transform: `translateY(${translateY}px) translateX(${translateX}px) scaleX(${scaleX}) scaleY(${scaleY})`,
    };
  }

  const [sourceRect, setSourceRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (button && button.current && isOpen) {
      setSourceRect(button.current.getBoundingClientRect());
    }
  }, [isOpen, button]);

  useEffect(() => {
    if (!sourceRect) return;

    const cleanup: { animationFrame?: number; timeout?: number }[] = [];

    if (isOpen) {
      setModalStyle((x) => ({
        ...x,
        visibility: "hidden",
      }));

      setModalWrapperStyle((x) => ({
        ...x,
        display: "",
        opacity: 0,
      }));

      const animationFrame1 = requestAnimationFrame(() => {
        setModalWrapperStyle((x) => ({
          ...x,
          opacity: 1,
        }));

        setModalPlaceholderStyle((x) => ({
          ...x,
          display: "",
          ...getModalPlaceholderPlacement(sourceRect),
        }));

        const animationFrame2 = requestAnimationFrame(() => {
          setModalPlaceholderStyle((x) => ({
            ...x,
            transform: "none",
          }));

          const timeout1 = window.setTimeout(() => {
            setModalStyle((x) => ({
              ...x,
              visibility: null,
            }));

            setModalPlaceholderStyle((x) => ({
              ...x,
              display: "none",
            }));
          }, ANIMATION_TIMEOUT_MS);

          cleanup.push({ timeout: timeout1 });
        });

        cleanup.push({ animationFrame: animationFrame2 });
      });

      cleanup.push({ animationFrame: animationFrame1 });
    } else {
      setModalPlaceholderStyle((x) => ({
        ...x,
        display: "",
      }));

      setModalStyle((x) => ({
        ...x,
        visibility: "hidden",
      }));

      const animationFrame1 = requestAnimationFrame(() => {
        setModalWrapperStyle((x) => ({
          ...x,
          opacity: 0,
        }));

        setModalPlaceholderStyle((x) => ({
          ...x,
          ...getModalPlaceholderPlacement(sourceRect),
        }));

        const timeout1 = window.setTimeout(() => {
          setModalWrapperStyle((x) => ({
            ...x,
            display: "none",
          }));

          setModalPlaceholderStyle((x) => ({
            ...x,
            display: "none",
          }));
        }, ANIMATION_TIMEOUT_MS);

        cleanup.push({ timeout: timeout1 });
      });

      cleanup.push({ animationFrame: animationFrame1 });
    }

    return () => {
      for (const { timeout, animationFrame } of cleanup) {
        if (timeout) clearTimeout(timeout);
        if (animationFrame) cancelAnimationFrame(animationFrame);
      }
    };
  }, [isOpen, sourceRect]);

  useEffect(() => {
    if (disableBodyScroll) {
      const original = document.body.style.overflowY;

      document.body.style.overflowY = "hidden";

      const cleanup: Function[] = [];

      cleanup.push(() => (document.body.style.overflowY = original));

      const hasScrollBar =
        document.body.scrollHeight >
        (document.body.parentElement?.clientHeight || 0);

      if (hasScrollBar) {
        const originalPaddingRight = document.body.style.paddingRight;

        document.body.style.paddingRight = `${getScrollbarWidth()}px`;

        cleanup.unshift(
          () => (document.body.style.paddingRight = originalPaddingRight)
        );
      }

      return () => {
        for (const fn of cleanup) {
          fn();
        }
      };
    }
  }, [disableBodyScroll]);

  useEffect(() => {
    if (isOpen) setDisableBodyScroll(true);
    else {
      const timeout = setTimeout(
        () => setDisableBodyScroll(false),
        ANIMATION_TIMEOUT_MS
      );

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isOpen]);

  return ReactDOM.createPortal(
    <ModalWrapper
      style={modalWrapperStyle}
      onClick={onClose}
      floatingCloseButton={floatingCloseButton || false}
      invertClose={invertClose}
    >
      <ModalContainer>
        <ModalElement className={className} style={modalPlaceholderStyle} />
        <ModalElement
          ref={modal}
          className={className}
          style={modalStyle}
          onClick={(event) => event.stopPropagation()}
        >
          <ModalHeader>
            <h2>{title}</h2>
            <button onClick={onClose}>
              <DismissIcon size={18} color="black" />
            </button>
          </ModalHeader>
          {children}
        </ModalElement>
      </ModalContainer>
    </ModalWrapper>,
    document.body
  );
}
